import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import {
  deleteNpcEntryAction,
  getNpcTeamEntries,
  getNpcTeamEntriesEditable,
  resetAddNpcEntryAction,
  resetDeleteNpcEntryAction,
  resetEditNpcEntryAction,
  resetReviewNpcEntryAction,
  reviewNpcEntryAction,
} from 'src/redux/actions/npcLogActions';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import npcLogService from 'src/services/apiServices/npcLogService/npcLogService';
import { StatusPill } from './npc-status';
import NpcReviewPrintDialog from './npc-review-print-dialog';
import EditNpcLog from './edit-npc-log';
import DeleteConfirmationModal from './delete-confirmation-modal';
import { usePermission } from 'src/hooks/use-permission';

const fmtDate = (d: string) => (d ? format(new Date(`${d}T00:00:00`), 'MMM d, yyyy') : '—');

// Prototype design tokens (THIN-7742).
const cardSx = {
  borderRadius: '20px',
  boxShadow: '0px 5px 22px rgba(0,0,0,0.04), 0 0 0 0.5px rgba(0,0,0,0.06)',
  overflow: 'hidden',
};
const headCellSx = {
  textTransform: 'uppercase',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '.5px',
  color: '#2F3746',
  bgcolor: '#F8F9FA',
  whiteSpace: 'nowrap',
};
const approveSx = {
  borderRadius: '9px',
  textTransform: 'none',
  boxShadow: 'none',
  bgcolor: '#10B981',
  '&:hover': { bgcolor: '#0E9E77', boxShadow: 'none' },
};
const declineSx = {
  borderRadius: '9px',
  textTransform: 'none',
  color: '#B42318',
  borderColor: 'rgba(240,68,56,0.45)',
  '&:hover': { borderColor: '#F04438', bgcolor: 'rgba(240,68,56,0.04)' },
};
const outlineSx = { borderRadius: '9px', textTransform: 'none' };

// Small count pill for the status tabs.
const TabCount = ({ n }: { n: number }) => (
  <Box
    component="span"
    sx={{
      ml: 1,
      fontSize: 11,
      fontWeight: 600,
      color: '#6C737F',
      bgcolor: '#F8F9FA',
      border: '1px solid #E5E7EB',
      borderRadius: '10px',
      px: 0.75,
      lineHeight: '18px',
    }}
  >
    {n}
  </Box>
);

// DOR/RDO/RSM review dashboard (THIN-7742), matching the validated prototype: underline status
// tabs with counts, therapist filter, bulk select, and per-status row actions. Gated upstream on
// VIEW_TEAM_NPC (or APPROVE_NPC) — THIN-8432.
const NpcReview = () => {
  const dispatch = useDispatch();
  // THIN-8330 / THIN-8432: each action is gated on its own permission. Adding an entry on behalf
  // of a team member, and editing/deleting a team entry, require EDIT_TEAM_NPC. Approve/decline
  // (single and bulk, including the row checkboxes) require APPROVE_NPC. View alone gets the
  // list, tabs, therapist filter and print.
  const canEditTeamNpc = usePermission('EDIT_TEAM_NPC');
  const canApproveNpc = usePermission('APPROVE_NPC');
  const canViewNpc = usePermission('VIEW_TEAM_NPC');

  const plainList = useSelector((s: any) => s.getNpcTeamEntriesReducer);
  const editableList = useSelector((s: any) => s.getNpcTeamEntriesEditableReducer);
  const list = canViewNpc ? editableList : plainList;
  const reviewRes = useSelector((s: any) => s.reviewNpcEntryReducer);
  const addRes = useSelector((s: any) => s.addNpcEntryReducer);
  const editRes = useSelector((s: any) => s.editNpcEntryReducer);
  const deleteRes = useSelector((s: any) => s.deleteNpcEntryReducer);

  const [range, setRange] = useState(() => {
    const today = new Date();
    return {
      from: format(startOfWeek(today, { weekStartsOn: 0 }), 'yyyy-MM-dd'),
      to: format(endOfWeek(today, { weekStartsOn: 0 }), 'yyyy-MM-dd'),
    };
  });
  const [tab, setTab] = useState('ALL');
  const [therapist, setTherapist] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [decline, setDecline] = useState<any>(null); // { mode: 'single'|'bulk', ids: string[] }
  const [declineReason, setDeclineReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<any>({ open: false, msg: '', severity: 'success' });
  const [printOpen, setPrintOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // THIN-8228: reviewer edit/delete of a team entry. editTarget opens EditNpcLog in EDIT mode;
  // deleteTarget opens the confirm modal.
  const [editTarget, setEditTarget] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const refresh = () =>
    dispatch(
      canViewNpc
        ? getNpcTeamEntriesEditable({ from: range.from, to: range.to })
        : getNpcTeamEntries({ from: range.from, to: range.to })
    );

  useEffect(() => {
    refresh();
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, canViewNpc]);

  useEffect(() => {
    const st = reviewRes?.response?.status;
    if (st === 200 || st === 201) {
      setToast({ open: true, msg: 'Entry updated.', severity: 'success' });
      setDecline(null);
      setDeclineReason('');
      refresh();
      dispatch(resetReviewNpcEntryAction());
    } else if (reviewRes?.response && st) {
      setToast({
        open: true,
        msg: reviewRes?.response?.response?.message || 'Something went wrong. Please try again.',
        severity: 'error',
      });
      dispatch(resetReviewNpcEntryAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewRes]);

  // THIN-8187: reviewer add-for-team result. EditNpcLog dispatches addNpcEntryAction; watch the
  // shared add reducer to toast, close the dialog, and refresh the team list.
  useEffect(() => {
    const st = addRes?.response?.status;
    if (st === 200 || st === 201) {
      setToast({ open: true, msg: 'NPC entry added.', severity: 'success' });
      setAddOpen(false);
      refresh();
      dispatch(resetAddNpcEntryAction());
    } else if (addRes?.response && st) {
      setToast({
        open: true,
        msg: addRes?.response?.response?.message || 'Something went wrong. Please try again.',
        severity: 'error',
      });
      dispatch(resetAddNpcEntryAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRes]);

  // THIN-8228: reviewer edit result. EditNpcLog (EDIT mode) dispatches editNpcEntryAction; close the
  // edit dialog and refresh the team list on success.
  useEffect(() => {
    const st = editRes?.response?.status;
    if (st === 200 || st === 201) {
      setToast({ open: true, msg: 'NPC entry updated.', severity: 'success' });
      setEditTarget(null);
      refresh();
      dispatch(resetEditNpcEntryAction());
    } else if (editRes?.response && st) {
      setToast({
        open: true,
        msg: editRes?.response?.response?.message || 'Something went wrong. Please try again.',
        severity: 'error',
      });
      dispatch(resetEditNpcEntryAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRes]);

  // THIN-8228: reviewer delete result.
  useEffect(() => {
    const st = deleteRes?.response?.status;
    if (st === 200 || st === 201) {
      setToast({ open: true, msg: 'NPC entry deleted.', severity: 'success' });
      setDeleteTarget(null);
      refresh();
      dispatch(resetDeleteNpcEntryAction());
    } else if (deleteRes?.response && st) {
      setToast({
        open: true,
        msg: deleteRes?.response?.response?.message || 'Something went wrong. Please try again.',
        severity: 'error',
      });
      dispatch(resetDeleteNpcEntryAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRes]);

  // THIN-8233: guard against a non-array `response` (a failed team-entries fetch used to store the
  // raw error here) so the .filter/.map below can't throw and blank the page behind the ErrorBoundary.
  const all: any[] = useMemo(
    () => (Array.isArray(list?.response) ? list.response : []),
    [list?.response]
  );
  const counts = useMemo(
    () => ({
      ALL: all.length,
      NOT_VALIDATED: all.filter((e) => e.status === 'NOT_VALIDATED').length,
      APPROVED: all.filter((e) => e.status === 'APPROVED').length,
      DECLINED: all.filter((e) => e.status === 'DECLINED').length,
    }),
    [all]
  );
  const therapists = useMemo(
    () => Array.from(new Set(all.map((e) => e.therapistName).filter(Boolean))).sort(),
    [all]
  );
  const visible = useMemo(
    () =>
      all.filter(
        (e) => (tab === 'ALL' || e.status === tab) && (!therapist || e.therapistName === therapist)
      ),
    [all, tab, therapist]
  );

  // Filters forwarded to the printable report so the PDF matches what's on screen. The therapist
  // filter is a name in the UI; resolve it back to the therapistId the endpoint expects.
  const printFilters = useMemo(
    () => ({
      from: range.from,
      to: range.to,
      status: tab === 'ALL' ? undefined : tab,
      therapistId: therapist
        ? all.find((e) => e.therapistName === therapist)?.therapistUuid
        : undefined,
    }),
    [range.from, range.to, tab, therapist, all]
  );

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allVisibleSelected = visible.length > 0 && visible.every((e) => selected.has(e.uuid));
  const toggleAll = () =>
    setSelected(allVisibleSelected ? new Set() : new Set(visible.map((e) => e.uuid)));

  const single = (id: string, action: string) => dispatch(reviewNpcEntryAction({ id, action }));

  // Bulk approve/decline in one transactional request (THIN-7818).
  const bulk = async (action: string, reason?: string) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBusy(true);
    try {
      // Backend expects the NpcReviewAction enum name (APPROVE/DECLINE), not the button verb.
      await npcLogService.reviewNpcBatch({
        ids,
        action: action.toUpperCase(),
        declineReason: reason,
      });
      setToast({
        open: true,
        msg: `${ids.length} ${ids.length === 1 ? 'entry' : 'entries'} updated.`,
        severity: 'success',
      });
    } catch (e: any) {
      setToast({
        open: true,
        msg: e?.response?.data?.message || 'Some entries could not be updated.',
        severity: 'error',
      });
    } finally {
      setBusy(false);
      setSelected(new Set());
      setDecline(null);
      setDeclineReason('');
      refresh();
    }
  };

  const openSingleDecline = (id: string) => {
    setDeclineReason('');
    setDecline({ mode: 'single', ids: [id] });
  };
  const openBulkDecline = () => {
    setDeclineReason('');
    setDecline({ mode: 'bulk', ids: Array.from(selected) });
  };
  const submitDecline = () => {
    if (!decline) return;
    if (decline.mode === 'bulk') bulk('decline', declineReason);
    else dispatch(reviewNpcEntryAction({ id: decline.ids[0], action: 'decline', declineReason }));
  };

  const loading = list?.loading;

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2,
          minHeight: 44,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 13.5,
            minHeight: 44,
            px: 2,
          },
        }}
      >
        <Tab
          value="ALL"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              All
              <TabCount n={counts.ALL} />
            </Box>
          }
        />
        <Tab
          value="NOT_VALIDATED"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              Needs review
              <TabCount n={counts.NOT_VALIDATED} />
            </Box>
          }
        />
        <Tab
          value="APPROVED"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              Approved
              <TabCount n={counts.APPROVED} />
            </Box>
          }
        />
        <Tab
          value="DECLINED"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              Declined
              <TabCount n={counts.DECLINED} />
            </Box>
          }
        />
      </Tabs>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Chip
          variant="outlined"
          label={`${visible.length} ${visible.length === 1 ? 'entry' : 'entries'}`}
          sx={{ borderRadius: '20px', fontWeight: 600, color: '#6C737F' }}
        />
        <TextField
          label="Therapist"
          select
          size="small"
          value={therapist}
          onChange={(e) => setTherapist(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All therapists</MenuItem>
          {therapists.map((t) => (
            <MenuItem
              key={t}
              value={t}
            >
              {t}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <TextField
          label="From"
          type="date"
          size="small"
          value={range.from}
          onChange={(e) => setRange((r: any) => ({ ...r, from: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={range.to}
          onChange={(e) => setRange((r: any) => ({ ...r, to: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined"
          startIcon={<PrintOutlinedIcon />}
          onClick={() => setPrintOpen(true)}
          sx={outlineSx}
        >
          Print / Export
        </Button>
        {canEditTeamNpc && (
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => setAddOpen(true)}
          >
            Add Entry
          </Button>
        )}
      </Stack>

      {/* THIN-8432: bulk select + Approve/Decline selected is an Approve-only action. */}
      {canApproveNpc && selected.size > 0 && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            mb: 1.75,
            py: 1.25,
            px: 2,
            borderRadius: '14px',
            bgcolor: '#EBEEFE',
            border: '1px solid #C7D0FB',
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600 }}
          >
            {selected.size} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            onClick={() => bulk('approve')}
            sx={approveSx}
          >
            Approve selected
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={openBulkDecline}
            sx={declineSx}
          >
            Decline selected
          </Button>
          <Button
            size="small"
            onClick={() => setSelected(new Set())}
            sx={{ borderRadius: '9px', textTransform: 'none', color: '#6C737F' }}
          >
            Clear
          </Button>
        </Stack>
      )}

      {loading ? (
        <Card sx={{ ...cardSx, p: 2 }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton
              key={i}
              height={52}
            />
          ))}
        </Card>
      ) : (
        <Card sx={cardSx}>
          <Table sx={{ '& td': { py: 1.75 } }}>
            <TableHead>
              <TableRow>
                {canApproveNpc && (
                  <TableCell
                    padding="checkbox"
                    sx={headCellSx}
                  >
                    <Checkbox
                      checked={allVisibleSelected}
                      indeterminate={selected.size > 0 && !allVisibleSelected}
                      onChange={toggleAll}
                    />
                  </TableCell>
                )}
                <TableCell sx={headCellSx}>Therapist</TableCell>
                <TableCell sx={headCellSx}>Date</TableCell>
                <TableCell sx={headCellSx}>Activity</TableCell>
                <TableCell sx={headCellSx}>Patient</TableCell>
                <TableCell sx={headCellSx}>Duration</TableCell>
                <TableCell sx={headCellSx}>Comment</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell
                  align="right"
                  sx={headCellSx}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canApproveNpc ? 9 : 8}>
                    <Typography
                      color="text.secondary"
                      align="center"
                      sx={{ py: 4 }}
                    >
                      No NPC entries for this filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {visible.map((e) => (
                <TableRow
                  key={e.uuid}
                  hover
                  selected={selected.has(e.uuid)}
                >
                  {canApproveNpc && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(e.uuid)}
                        onChange={() => toggleRow(e.uuid)}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600 }}>{e.therapistName || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(e.date)}</TableCell>
                  <TableCell>{e.activityTypeLabel}</TableCell>
                  <TableCell>{e.patientName || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.durationMinutes} min</TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: 'normal', color: '#6C737F' }}>
                    {e.comment || '—'}
                  </TableCell>
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <StatusPill status={e.status} />
                      {e.status === 'DECLINED' && e.declineReason && (
                        <Tooltip title={e.declineReason}>
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ cursor: 'help', textDecoration: 'underline dotted' }}
                          >
                            reason
                          </Typography>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      {e.status === 'NOT_VALIDATED' && canApproveNpc && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => single(e.uuid, 'approve')}
                            sx={approveSx}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openSingleDecline(e.uuid)}
                            sx={declineSx}
                          >
                            Decline
                          </Button>
                        </>
                      )}
                      {e.status === 'APPROVED' && canApproveNpc && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          onClick={() => single(e.uuid, 'reopen')}
                          sx={outlineSx}
                        >
                          Unapprove
                        </Button>
                      )}
                      {e.status === 'DECLINED' && canApproveNpc && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => single(e.uuid, 'approve')}
                            sx={approveSx}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            onClick={() => single(e.uuid, 'reopen')}
                            sx={outlineSx}
                          >
                            Reopen
                          </Button>
                        </>
                      )}
                      {/* THIN-8228: reviewer edit/delete of any team entry, mirroring Labor Hour
                          Management. Editing re-opens the entry for review (backend resets to
                          NOT_VALIDATED); available on every status.
                       */}
                      {canEditTeamNpc && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => setEditTarget(e)}
                              aria-label="edit"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteTarget(e)}
                              aria-label="delete"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog
        open={!!decline}
        onClose={() => setDecline(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle>
          {decline?.mode === 'bulk'
            ? `Decline ${decline?.ids?.length} entries`
            : 'Decline NPC entry'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Reason (shown to the therapist)"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDecline(null)}
            sx={{ borderRadius: '9px', textTransform: 'none', color: '#6C737F' }}
          >
            Cancel
          </Button>
          <Button
            onClick={submitDecline}
            variant="contained"
            disabled={busy || reviewRes?.loading}
            sx={{
              borderRadius: '9px',
              textTransform: 'none',
              boxShadow: 'none',
              bgcolor: '#F04438',
              '&:hover': { bgcolor: '#D92D20', boxShadow: 'none' },
            }}
          >
            Decline
          </Button>
        </DialogActions>
      </Dialog>

      <NpcReviewPrintDialog
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        filters={printFilters}
      />

      <EditNpcLog
        open={addOpen}
        mode="ADD"
        reviewer
        onClose={() => setAddOpen(false)}
      />

      {/* THIN-8228: reviewer edit of a team entry. EDIT mode never reassigns the therapist. */}
      <EditNpcLog
        open={!!editTarget}
        mode="EDIT"
        entry={editTarget}
        reviewer
        onClose={() => setEditTarget(null)}
      />

      <DeleteConfirmationModal
        open={!!deleteTarget}
        loading={deleteRes?.loading}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && dispatch(deleteNpcEntryAction(deleteTarget.uuid))}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t: any) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t: any) => ({ ...t, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NpcReview;
